// src/components/InstructorDashboard.js

import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

export default function InstructorDashboard() {
  const navigate = useNavigate();

  // Logged-in user's ID
  const instructorId = localStorage.getItem("userId");

  const [backendUrl, setBackendUrl] = useState("");
  const [sessions, setSessions] = useState([]);
  const [students, setStudents] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [absentees, setAbsentees] = useState({});

  // ---------------------------------------------------------
  // LOAD BACKEND URL
  // ---------------------------------------------------------
  useEffect(() => {
    const script = document.createElement("script");

    script.src = "/config.js";

    script.onload = () => {
      if (window.env?.BACKEND_URL) {
        setBackendUrl(window.env.BACKEND_URL);
      } else {
        console.error("❌ BACKEND_URL not found in config.js");
      }
    };

    script.onerror = () => {
      console.error("❌ Unable to load config.js");
    };

    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  // ---------------------------------------------------------
  // FETCH SESSIONS & MATERIALS
  // ---------------------------------------------------------
  useEffect(() => {
    if (instructorId && backendUrl) {
      fetchSessions();
      fetchMaterials();
    }
  }, [instructorId, backendUrl]);

  // ---------------------------------------------------------
  // FETCH ASSIGNED SESSIONS
  // ---------------------------------------------------------
  const fetchSessions = async () => {
    try {
      const res = await axios.get(
        `${backendUrl}/instructor/${instructorId}/sessions`
      );

      setSessions(res.data);
    } catch (err) {
      console.error("❌ Error fetching sessions:", err);
    }
  };

  // ---------------------------------------------------------
  // FETCH COURSE MATERIALS
  // ---------------------------------------------------------
  const fetchMaterials = async () => {
    try {
      const res = await axios.get(
        `${backendUrl}/instructor/${instructorId}/materials`
      );

      setMaterials(res.data);
    } catch (err) {
      console.error("❌ Error fetching materials:", err);
    }
  };

  // ---------------------------------------------------------
  // FETCH STUDENTS
  // ---------------------------------------------------------
  // Students are fetched using COURSE_ID.
  // ---------------------------------------------------------
  const fetchStudents = async (session) => {
    try {
      const res = await axios.get(
        `${backendUrl}/course/${session.Course_ID}/students`
      );

      setStudents(res.data);
      setSelectedSession(session);

      const initialAbsentees = {};

      res.data.forEach((s) => {
        initialAbsentees[s.User_ID] =
          s.AttendanceStatus === "Absent";
      });

      setAbsentees(initialAbsentees);
    } catch (err) {
      console.error("❌ Error fetching students:", err);
    }
  };

  // ---------------------------------------------------------
  // ATTENDANCE CHECKBOX
  // ---------------------------------------------------------
  const handleCheckboxChange = (userId) => {
    setAbsentees((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  // ---------------------------------------------------------
  // SAVE ATTENDANCE
  // ---------------------------------------------------------
  const saveAttendance = async () => {
    if (!selectedSession) return;

    try {
      const absentIds = Object.keys(absentees)
        .filter((id) => absentees[id])
        .map(Number);

      await axios.post(
        `${backendUrl}/session/${selectedSession.Session_ID}/attendance`,
        {
          absentIds,
        }
      );

      await fetchStudents(selectedSession);

      alert("✅ Attendance saved successfully!");
    } catch (err) {
      console.error("❌ Error saving attendance:", err);
      alert("Failed to save attendance.");
    }
  };

  // ---------------------------------------------------------
  // PAGE
  // ---------------------------------------------------------
  return (
    <div style={styles.container}>

      {/* =====================================================
          EXAM RESULTS
          ===================================================== */}
      <section style={styles.section}>
        <div style={styles.resultsWrapper}>
          <button
            onClick={() => navigate("/instructor/results")}
            style={styles.button}
          >
            📊 View Exam Results
          </button>
        </div>
      </section>

      {/* =====================================================
          ASSIGNED SESSIONS
          ===================================================== */}
      <section style={styles.section}>
        <h3 style={styles.subHeader}>
          🗂️ Assigned Sessions
        </h3>

        <div style={styles.card}>
          {sessions.length === 0 ? (
            <p style={styles.warningText}>
              ⚠️ No sessions assigned.
            </p>
          ) : (
            <>
              <div style={styles.tableHeader}>
                <div style={styles.colCourse}>
                  Course
                </div>

                <div style={styles.colDate}>
                  Dates
                </div>

                <div style={styles.colAction}></div>
              </div>

              {sessions.map((s) => (
                <div
                  key={s.Session_ID}
                  style={styles.tableRow}
                >
                  <div style={styles.colCourse}>
                    <strong>
                      {s.Course_Name}
                    </strong>{" "}
                    [{s.SessionName}]
                  </div>

                  <div style={styles.colDate}>
                    {new Date(
                      s.StartDate
                    ).toLocaleDateString()}{" "}
                    –{" "}
                    {new Date(
                      s.EndDate
                    ).toLocaleDateString()}
                  </div>

                  <div style={styles.colAction}>
                    <button
                      onClick={() =>
                        fetchStudents(s)
                      }
                      style={styles.button}
                    >
                      👥 View Students
                    </button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </section>

      {/* =====================================================
          STUDENTS / ATTENDANCE
          ===================================================== */}
      {selectedSession && (
        <section style={styles.section}>
          <h3 style={styles.subHeader}>
            👥 Students –{" "}
            {selectedSession.Course_Name} [
            {selectedSession.SessionName}]
          </h3>

          <div style={styles.card}>
            {students.length === 0 ? (
              <p style={styles.warningText}>
                ⚠️ No students enrolled.
              </p>
            ) : (
              <>
                <div style={styles.tableHeader}>
                  <div style={styles.colStudent}>
                    Student
                  </div>

                  <div style={styles.colEmail}>
                    Email
                  </div>

                  <div style={styles.colStatus}>
                    Absent
                  </div>
                </div>

                {students.map((s) => (
                  <div
                    key={s.User_ID}
                    style={styles.tableRow}
                  >
                    <div style={styles.colStudent}>
                      {s.Username}
                    </div>

                    <div style={styles.colEmail}>
                      {s.Email}
                    </div>

                    <div style={styles.colStatus}>
                      <input
                        type="checkbox"
                        checked={
                          absentees[s.User_ID] ||
                          false
                        }
                        onChange={() =>
                          handleCheckboxChange(
                            s.User_ID
                          )
                        }
                      />
                    </div>
                  </div>
                ))}

                <button
                  onClick={saveAttendance}
                  style={styles.button}
                >
                  💾 Save Attendance
                </button>
              </>
            )}
          </div>
        </section>
      )}

      {/* =====================================================
          COURSE MATERIALS
          ===================================================== */}
      <section style={styles.section}>
        <h3 style={styles.subHeader}>
          📁 Course Materials
        </h3>

        <div style={styles.card}>
          {materials.length === 0 ? (
            <p style={styles.warningText}>
              ⚠️ No materials uploaded yet.
            </p>
          ) : (
            materials.map((file, idx) => (
              <div
                key={idx}
                style={styles.tableRow}
              >
                <strong>
                  {file.Course_Name}
                </strong>{" "}
                —{" "}

                <a
                  href={`${backendUrl}/uploads/${file.FileName}`}
                  target="_blank"
                  rel="noreferrer"
                  style={styles.link}
                >
                  📄{" "}
                  {file.Material_Name ||
                    file.FileName}
                </a>
              </div>
            ))
          )}
        </div>
      </section>

    </div>
  );
}

// =============================================================
// STYLES
// =============================================================

const styles = {
  container: {
    padding: 20,
    maxWidth: 1230,
    margin: "0 auto",
  },

  subHeader: {
    fontSize: "1.1rem",
    marginBottom: 5,
    color: "#E30016",
  },

  section: {
    marginBottom: 25,
  },

  resultsWrapper: {
    textAlign: "right",
    marginBottom: 15,
  },

  card: {
    backgroundColor: "#fafafa",
    padding: 20,
    borderRadius: 12,
  },

  warningText: {
    color: "#999",
    fontStyle: "italic",
  },

  button: {
    backgroundColor: "#E30016",
    color: "#fff",
    padding: "6px 14px",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
  },

  tableHeader: {
    display: "flex",
    fontWeight: "bold",
  },

  tableRow: {
    display: "flex",
    padding: "8px 0",
  },

  colCourse: {
    flex: 2,
  },

  colDate: {
    flex: 2,
  },

  colAction: {
    flex: 1,
  },

  colStudent: {
    flex: 2,
  },

  colEmail: {
    flex: 3,
  },

  colStatus: {
    flex: 1,
  },

  link: {
    color: "#E30016",
    fontWeight: 500,
  },
};